import {Component, effect, inject, signal, viewChild} from '@angular/core';
import {Button} from 'primeng/button';
import {Fluid} from 'primeng/fluid';
import {FormFieldComponent} from '../../../shared/components/inputs/form-field/form-field.component';
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {TranslocoDirective, TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {FormValidationService} from '../../../shared/services/form-validation.service';
import {
  AuthMode,
  InviteProviderControllerService,
  InviteProviderDTO,
  InviteProviderRequestParams,
  ProviderAdminControllerService,
} from '@frontend/shared-api';
import {RichTextEditorComponent} from '../../../shared/components/rich-text-editor/rich-text-editor.component';
import {ToastService} from '@frontend/toast';
import {Divider} from 'primeng/divider';
import {DirectusLoader, HrToolProvider} from '@frontend/directus';
import {CountrySelectComponent} from '../../../shared/components/inputs/country-select/country-select.component';
import {Message} from 'primeng/message';

@Component({
  selector: 'app-selecthor-admin-registration',
  templateUrl: './selecthor-admin-registration.component.html',
  imports: [
    Fluid,
    FormsModule,
    ReactiveFormsModule,
    FormFieldComponent,
    TranslocoPipe,
    Button,
    TranslocoDirective,
    RichTextEditorComponent,
    Divider,
    CountrySelectComponent,
    Message,
  ],
})
export class SelecthorAdminRegistrationComponent {
  private readonly fb = inject(FormBuilder);
  private readonly formValidation = inject(FormValidationService);
  private readonly inviteProviderControllerService = inject(InviteProviderControllerService);
  private readonly toast = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  private readonly directusLoader = inject(DirectusLoader);
  private readonly providerAdminService = inject(ProviderAdminControllerService);

  protected readonly hrToolProvidersOptions = signal<{label: string; value: string}[]>([]);
  protected readonly languageOptions = signal<{label: string; value: string}[]>([]);
  protected readonly authModeOptions: {label: string; value: AuthMode}[] = [
    {label: 'Local (password)', value: AuthMode.LOCAL},
    {label: 'SSO', value: AuthMode.SSO},
  ];
  private readonly allHrToolProviders = signal<HrToolProvider[]>([]);
  private readonly emailContentModified = signal(false);
  private readonly emailEditor = viewChild(RichTextEditorComponent);

  protected readonly selectedTemplateLanguage = signal<string | null>(null);

  currentLang = this.transloco.getActiveLang();
  submitting = false;

  protected isSubmitDisabled(): boolean {
    return this.form.invalid || !this.emailContentModified() || this.submitting;
  }

  readonly form = this.fb.group(
    {
      hrToolProviderDirectusId: ['', Validators.required],
      authMode: [AuthMode.LOCAL, Validators.required],
      templateLanguage: [''],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      street: ['', Validators.required],
      houseNumber: ['', Validators.required],
      zipCode: ['', [Validators.required, this.formValidation.numbersOnlyValidator()]],
      city: ['', Validators.required],
      countryCode: ['', Validators.required],
      subject: ['', Validators.required],
      emailText: ['', Validators.required],
    },
    {
      validators: this.formValidation.passwordMatchValidator(),
    },
  );

  constructor() {
    effect(() => {
      this.loadAvailableHrTools();
      this.loadLanguageOptions();
    });

    this.form.get('templateLanguage')?.valueChanges.subscribe((value) => {
      this.selectedTemplateLanguage.set(value);
    });
  }

  onEmailContentChange(content: string): void {
    this.form.patchValue({emailText: content});
    this.emailContentModified.set(true);
  }

  protected onTemplateLoaded(templateName: string): void {
    this.form.patchValue({subject: templateName});
  }

  protected onContentReset(resetData: {content: string; templateName: string}): void {
    this.form.patchValue({subject: resetData.templateName});
  }

  onSubmit() {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        const control = this.form.get(key);
        control?.markAllAsTouched();
      });
      return;
    }

    this.submitting = true;
    const formValues = this.form.value;

    const inviteProviderRequest: InviteProviderRequestParams = {
      inviteProviderDTO: this.buildInviteProviderRequest(formValues),
    };

    this.inviteProviderControllerService.inviteProvider(inviteProviderRequest).subscribe({
      next: () => {
        this.submitting = false;
        this.toast.showSuccess('selecthorAdmin.registrationSuccess');
        this.restoreInitialState();
      },
    });
  }

  private restoreInitialState(): void {
    this.form.reset({authMode: AuthMode.LOCAL, templateLanguage: this.currentLang});
    this.emailContentModified.set(false);
    this.emailEditor()?.restoreTemplate(this.currentLang);
  }

  private loadAvailableHrTools() {
    const hrToolProviders$ = this.providerAdminService.loadAvailableProvidersForRegistration();
    hrToolProviders$.subscribe((providers) => {
      this.allHrToolProviders.set(
        providers.map((provider) => {
          return {
            id: provider.providerDirectusId,
            name: provider.name,
            providerDirectusId: provider.providerDirectusId,
          } as HrToolProvider;
        }),
      );
      const options = this.allHrToolProviders()
        .sort((p1, p2) => p1.name.localeCompare(p2.name))
        .map((provider) => ({
          label: provider.name,
          value: provider.id,
        }));
      this.hrToolProvidersOptions.set(options);
    });
  }

  private buildInviteProviderRequest(formValues: Record<string, string | null>): InviteProviderDTO {
    const selectedHrToolProviderId = formValues['hrToolProviderDirectusId'] as string;
    const selectedHrToolProvider = this.allHrToolProviders().filter(
      (provider) => provider.providerDirectusId === selectedHrToolProviderId,
    )[0];

    return {
      tenant: selectedHrToolProvider?.name ?? 'unknown',
      firstName: formValues['firstName'] as string,
      lastName: formValues['lastName'] as string,
      email: formValues['email'] as string,
      street: formValues['street'] as string,
      houseNumber: formValues['houseNumber'] as string,
      zipCode: formValues['zipCode'] as string,
      city: formValues['city'] as string,
      countryCode: formValues['countryCode'] as string,
      emailText: formValues['emailText'] as string,
      subject: formValues['subject'] as string,
      languageCode: (formValues['templateLanguage'] as string) || this.currentLang,
      hrToolProviderDirectusId: selectedHrToolProviderId,
      authMode: (formValues['authMode'] as AuthMode) ?? AuthMode.LOCAL,
    };
  }

  private loadLanguageOptions() {
    const currentLang = this.transloco.getActiveLang();
    const options = this.directusLoader.availableLanguages.map((lang) => ({
      label: lang.code.toUpperCase(),
      value: lang.code,
    }));
    this.languageOptions.set(options);

    const currentValue = this.form.get('templateLanguage')?.value;
    if (!currentValue) {
      this.form.patchValue({templateLanguage: currentLang});
      this.selectedTemplateLanguage.set(currentLang);
    }
  }
}
